import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import { useUser } from "../contexts/UserContext";
import InvoicePopup from "./InvoicePopup";
import api from "../utils/api";
import Loader from "./Loader";

function TitleCell({ title }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(title || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="text-sm text-left w-full" style={{ display: 'block' }}>
      {title}
      <span data-copy-wrap style={{ display: 'inline-block', marginLeft: '5px', verticalAlign: 'middle', lineHeight: 0 }}>
        <button
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy title'}
          className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
          style={{ lineHeight: 0, background: 'none', border: 'none', padding: 0 }}
        >
          {copied
            ? <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700, lineHeight: 1 }}>✓</span>
            : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          }
        </button>
      </span>
    </span>
  );
}

// Custom pagination: uses DataTable's count/state, keeps current footer look
const CustomPagination = ({ currentPage, rowCount, rowsPerPage, onChangePage }) => {
  if (!rowCount || rowCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, rowCount);

  // Pagination logic: show a small window of pages
  const getPageNumbers = () => {
    const pages = [];
    const isMobile = window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Ensure we don't go below 1
    startPage = Math.max(1, startPage);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
      <div className="text-sm text-gray-600 order-2 sm:order-1">
        Showing <span className="font-semibold">{start}-{end}</span> of <span className="font-semibold">{rowCount}</span>
      </div>
      <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">
        <button
          type="button"
          onClick={() => onChangePage(1)}
          disabled={currentPage <= 1}
          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          <span className="hidden sm:inline">First</span>
          <span className="sm:hidden text-lg">«</span>
        </button>

        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            type="button"
            onClick={() => onChangePage(pageNum)}
            className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs sm:text-sm border rounded font-medium transition-all ${currentPage === pageNum
                ? "bg-red-600 text-white border-red-600 shadow-sm scale-110 z-10"
                : "border-gray-300 hover:bg-gray-100 bg-white text-gray-700 hover:border-gray-400"
              }`}
          >
            {pageNum}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          <span className="hidden sm:inline">Last</span>
          <span className="sm:hidden text-lg">»</span>
        </button>
      </div>
    </div>
  );
};

// Loading Spinner Component - uses shared Loader
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-20 min-h-[400px]">
    <Loader />
  </div>
);

const TicketTable = React.memo(({ 
  tickets, 
  onTicketClick, 
  isLoading = false, 
  initialRowsPerPage = 10, 
  rowsPerPage, 
  onRowsPerPageChange,
  totalRows,
  currentPage,
  onPageChange
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [clientData, setclientData] = useState(null);
  const [clientInvoice, setclientInvoice] = useState(null);

  const loadTicketsData = useCallback(async () => {
    if (selectedTicket <= 0) return;
    try {
      const res = await api.get("/ticket/getClientDetail", {
        params: {
          selectedTicket: selectedTicket,
        },
      });
      if (res.data) {
        if (res.data.data.client_data) {
          setclientData(res.data.data.client_data);
        }
        if (res.data.data.invoice) {
          setclientInvoice(res.data.data.invoice);
        }
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (selectedTicket > 0) {
      loadTicketsData();
    }
  }, [selectedTicket, loadTicketsData]);

  const perPage = rowsPerPage || initialRowsPerPage;

  const { user } = useUser();

  // Memoize formatDateWithTime to prevent recreation
  const formatDateWithTime = useCallback((dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const time = `${hours}:${minutes} ${ampm}`;
    return `${day}-${month}-${year} ${time}`;
  }, []);

  // Memoize getDateAgeClass
  const getDateAgeClass = useCallback((dateString) => {
    if (!dateString) return "";

    const created = new Date(dateString);
    const now = new Date();

    // Difference in days
    const diffTime = now - created;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    // Apply Tailwind classes
    if (diffDays > 180) {
      return "text-red-600 font-bold"; // > 6 months
    } else if (diffDays > 90) {
      return "text-red-500 font-bold"; // > 3 months
    }
    return "";
  }, []);

  const handleRowClick = useCallback((ticket) => {
    if (typeof onTicketClick === "function") {
      onTicketClick(ticket);
    }
  }, [onTicketClick]);

  const handleCollegeNameClick = useCallback((ticket, e) => {
    if (e) e.stopPropagation();
    // Old logic: only role_id == 1 opens getClientDetails popup; others get ticket details
    if (user?.role_id == 1) {
      setSelectedTicket(ticket.id);
      setIsPopupOpen(true);
    } else {
      handleRowClick(ticket);
    }
  }, [user?.role_id, handleRowClick]);


  const handleRowClicked = useCallback((row, e) => {
    // Don't open ticket if click came from the copy button area
    if (e?.target?.closest?.('[data-copy-wrap]')) return;
    const isCollege =
      e?.target?.closest?.(".college-name-cell") ||
      e?.target?.closest?.('[data-column-id="college"]');
    if (isCollege) {
      handleCollegeNameClick(row, e);
    } else {
      handleRowClick(row);
    }
  }, [handleCollegeNameClick, handleRowClick]);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  // Define columns for DataTable - more compact
  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "Ticket ID",
        selector: (row) => Number(row.id) || 0,
        sortable: true,
        width: "80px",
        compact: true,
        cell: (row) => (
          <div className="text-center">
            <div className="font-semibold text-gray-800 text-sm">{row.id}</div>
            <div
              className={`mt-0.5 inline-block px-1.5 py-0.5 rounded-full text-white text-xs ${row.priority === "Critical"
                ? "bg-red-600"
                : row.priority === "High"
                  ? "bg-orange-500"
                  : row.priority === "Medium"
                    ? "bg-blue-600"
                    : row.priority === "Low"
                      ? "bg-green-600"
                      : "bg-gray-400"
                }`}
            >
              {row.priority}
            </div>
          </div>
        ),
      },
    ];

    // Add College Name column if user is not role 2
    if (user?.role_id !== "2") {
      baseColumns.push({
        name: "Customer",
        id: "college",
        selector: (row) => row.collegeName || "",
        sortable: true,
        width: "130px",
        compact: true,
        cell: (row) => (
          <div
            className="flex flex-col text-center cursor-pointer college-name-cell"
            data-tag="allowRowEvents"
            onClick={(e) => handleCollegeNameClick(row, e)}
          >
            <div className="font-bold uppercase text-gray-800 truncate text-xs" data-tag="allowRowEvents">
              {row.collegeName}
            </div>
            <div className="italic text-gray-600 text-xs" data-tag="allowRowEvents">
              {row.customer_name}
            </div>
          </div>
        ),
      });
    }

    baseColumns.push(
      {
        name: "Module",
        selector: (row) => row.module || "",
        sortable: true,
        width: "110px",
        compact: true,
        cell: (row) => (
          <div className="text-center">
            <div className="font-medium text-gray-800 text-xs">{row.module}</div>
            <div
              className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${((row.status === "New") && !(row.status_id >= 7 && Number(user?.role_id) === 2))
                ? "bg-blue-100 text-black"
                : (row.status === "IN process" || (row.status_id >= 7 && Number(user?.role_id) === 2))
                  ? "bg-green-100 text-black"
                  : row.status === "Pending verification & closure"
                    ? "bg-purple-100 text-black"
                  : Number(row.status_id) === 12
                    ? "bg-cyan-100 text-black"
                  : Number(row.status_id) === 13
                    ? "bg-green-100 text-black"
                    : row.status_id == 7
                      ? "bg-pink-100 text-pink-700"
                    : row.status === "Closed"
                      ? "bg-red-400 text-white"
                      : row.status === "Reopen"
                        ? "bg-orange-200 text-black"
                        : ([8, 9, 10].includes(Number(row.status_id)))
                          ? "bg-indigo-100 text-black"
                          : "bg-gray-200 text-black"
                }`}
            >
              <span
                className={`w-1 h-1 rounded-full ${((row.status === "New") && !(row.status_id >= 7 && Number(user?.role_id) === 2))
                  ? "bg-cyan-900"
                  : (row.status === "IN process" || (row.status_id >= 7 && Number(user?.role_id) === 2))
                    ? "bg-green-600"
                    : row.status === "Pending verification & closure"
                      ? "bg-purple-700"
                    : Number(row.status_id) === 12
                      ? "bg-cyan-600"
                    : Number(row.status_id) === 13
                      ? "bg-green-600"
                      : row.status_id == 7
                        ? "bg-pink-600"
                      : row.status === "Closed"
                        ? "bg-red-600"
                        : row.status === "Reopen"
                          ? "bg-orange-600"
                          : "bg-gray-600"
                  }`}
              ></span>
              {(row.status_id >= 7 && row.status_id <= 11 && Number(user?.role_id) === 2)
                ? "IN process"
                : (row.status_id == 12 ? "Pending(Cust)" : (row.status_id == 13 ? "Req.Updated" : (row.status_id == 7 ? "Pending" : (row.status === "Pending verification & closure" ? "Pending" : (row.status === "Reopen" ? "Reopened" : row.status)))))}
            </div>
          </div>
        ),
      },
      {
        name: "Title",
        id: "title-column",
        selector: (row) => row.title || "",
        sortable: true,
        minWidth: "250px",
        compact: true,
        cell: (row) => <TitleCell title={row.title} />,
        style: {
          textAlign: "left",
        },
      },
      {
        name: "Assigned To",
        selector: (row) => row.assignedTo || "",
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="whitespace-normal text-center text-xs">
            {row.assignedTo?.toUpperCase()} <br />
            <span className="text-gray-500">
              {row.AssignedTo_designation_id === "2"
                ? "(ENOVA)"
                : row.collegeName &&
                `(${row.collegeName.toUpperCase()})`}
            </span>
          </div>
        ),
      },
      {
        name: "Reported By",
        selector: (row) => row.client || "",
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="whitespace-normal text-center text-xs">
            {row.client?.toUpperCase()} <br />
            <span className="text-gray-500">
              {row.ReportedBy_designation_id === "2"
                ? "(ENOVA)"
                : row.collegeName &&
                `(${row.collegeName.toUpperCase()})`}
            </span>
          </div>
        ),
      },
      {
        name: "ISSUE TYPE",
        selector: (row) => row.issue_type || "Yet to start",
        sortable: true,
        cell: (row) => <div className="text-xs whitespace-nowrap">{row.issue_type || "Yet to start"}</div>,
        width: "120px",
      },
      {
        name: "Created Date",
        id: "created",
        selector: (row) => row.created ? new Date(row.created).getTime() : 0,
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className={`text-xs ${getDateAgeClass(row.created)}`}>
            {formatDateWithTime(row.created)}
          </div>
        ),
      },
      {
        name: "Updated Date",
        selector: (row) => row.updated ? new Date(row.updated).getTime() : 0,
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="text-xs">{formatDateWithTime(row.updated)}</div>
        ),
      },
      {
        name: "Comments",
        selector: (row) => Number(row.comment_count) || 0,
        sortable: true,
        width: "80px",
        compact: true,
        cell: (row) => (
          <div className="text-center text-sm">{row.comment_count}</div>
        ),
      }
    );

    return baseColumns;
  }, [user?.role_id, formatDateWithTime, getDateAgeClass, handleCollegeNameClick]);

  // Custom styles for DataTable - neat center-aligned
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f3f4f6",
        color: "#374151",
        fontWeight: "600",
        fontSize: "0.75rem",
        borderBottom: "2px solid #d1d5db",
        minHeight: "45px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#e5e7eb",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        fontSize: "0.75rem",
        paddingTop: "16px",
        paddingBottom: "16px",
        textAlign: "center",
        verticalAlign: "middle",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        minHeight: "70px",
        borderBottom: "1px solid #e5e7eb",
        "&:hover": {
          backgroundColor: "#f9fafb",
        },
        "&:nth-of-type(even)": {
          backgroundColor: "#fafafa",
        },
      },
    },
    table: {
      style: {
        overflowX: "auto",
      },
    },
    tableWrapper: {
      style: {
        display: "table",
        width: "100%",
      },
    },
  };

  // Hide sort icons and bottom rows per page selector using CSS
  // Also ensure all cell content is center-aligned
  const hideSortIconStyle = `
    .rdt_TableHeadRow .rdt_TableCol > div > div:last-child,
    .rdt_TableHeadRow .rdt_TableCol > div > span:last-child,
    .rdt_TableHeadRow .rdt_TableCol svg,
    .rdt_TableHeadRow .rdt_TableCol [class*="sort"] {
      display: none !important;
      visibility: hidden !important;
    }
    /* Custom pagination: use our component's layout, minimal wrapper */
    .rdt_Pagination {
      padding: 0 !important;
      margin: 0 !important;
      min-height: 0 !important;
    }

    .rdt_TableRow {
      user-select: none;
    }
    .rdt_TableBody .rdt_TableRow .rdt_TableCell > * {
      pointer-events: none;
    }
    /* Re-enable pointer-events for copy button */
    .rdt_TableBody .rdt_TableCell [data-copy-wrap],
    .rdt_TableBody .rdt_TableCell [data-copy-wrap] * {
      pointer-events: auto !important;
      cursor: pointer !important;
    }
    /* Allow clicks on college name so getClientDetail runs (any depth inside cell) */
    .rdt_TableBody .rdt_TableCell .college-name-cell {
      pointer-events: auto !important;
      cursor: pointer !important;
    }

    /* Center-align all cell content except Title column */
    .rdt_TableBody .rdt_TableCell {
      text-align: center !important;
    }

    /* Title column cells - left aligned (target by class) */
    .rdt_TableBody .rdt_TableCell:has(.title-cell) {
      text-align: left !important;
    }

    .rdt_TableBody .rdt_TableCell > div {
      text-align: center !important;
      margin: 0 auto;
    }

    /* Override for title-cell specifically - left align */
    .rdt_TableBody .rdt_TableCell > div.title-cell {
      text-align: left !important;
      margin: 0 !important;
    }

    /* Center-align all table headers (including Title header) */
    .rdt_TableHeadRow .rdt_TableCol {
      text-align: center !important;
    }

    .rdt_TableHeadRow .rdt_TableCol > div {
      text-align: center !important;
      justify-content: center;
    }
  `;


  return (
    <div className="min-h-[calc(100vh-400px)]">
      {/* Container for both Mobile and Desktop */}
      <div className="w-full">
        <style>{hideSortIconStyle}</style>
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto relative min-h-[400px]">
            <DataTable
              key={`${perPage}-${tickets?.length}`}
              columns={columns}
              data={tickets || []}
              onRowClicked={handleRowClicked}
              progressPending={isLoading}
              progressComponent={<LoadingSpinner />}
              persistTableHead
              customStyles={customStyles}
              highlightOnHover
              pointerOnHover
              noDataComponent={
                <div className="flex justify-center items-center h-40 text-base sm:text-lg text-gray-600 py-8">
                  No tickets available at the moment
                </div>
              }
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              onChangePage={onPageChange}
              paginationComponent={CustomPagination}
              paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        </div>
      </div>

      <InvoicePopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        Ticket_id={selectedTicket}
        clientData={clientData}
        clientInvoice={clientInvoice}
      />
    </div>
  );
});

TicketTable.displayName = "TicketTable";

export default TicketTable;
